<!--
 * @FileDescription: Privacy Policy Page
 * @Author: jianguo Wang
 * @Date: 2025-01-07
-->
<template>
  <div class="page-wrapper">
    <headerPart />
    <main class="main-content">
      <div class="content-container">
        <router-link to="/" class="back-link">
          <svg class="back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Home
        </router-link>

        <article class="content-card">
          <h1 class="page-title">Privacy Policy</h1>

          <div class="content-body">
            <!-- Introduction -->
            <section class="content-section">
              <h2 class="section-heading">Introduction</h2>
              <p class="section-paragraph">Welcome to Apexis:</p>
              <p class="section-paragraph">
                Apexis is a financial application developed and operated by APEXIS PTE. LTD. We are committed to
                providing a secure and convenient global payment platform. Your privacy is extremely important to us.
              </p>
              <p class="section-paragraph">
                This Privacy Policy explains how we collect, use and protect your information when you use our mobile
                application and related services. By accessing or using Apexis, you agree to the terms of this policy.
              </p>
            </section>

            <!-- Purpose of Collection -->
            <section class="content-section">
              <h2 class="section-heading">Purpose of Collection</h2>
              <p class="section-paragraph">
                APEXIS PTE. LTD. will endeavor to maintain the confidentiality of personal data it holds. Unless you have
                given us prior permission, or such disclosure is within the scope of disclosure permitted under
                applicable laws/guidelines, we will not share your information with third parties for commercial
                purposes. We will take reasonable measures to protect the confidentiality of your information and
                maintain your privacy. The accuracy and currency of the personal data we hold about you is essential. If
                your personal data changes during your relationship with us, please inform us.
              </p>
              <p class="section-paragraph">
                Notwithstanding the foregoing, by submitting personal data, you agree that any such data may be used for
                the following purposes:
              </p>
              <ul class="content-list">
                <li>
                  Processing applications and day-to-day operations of services provided by APEXIS PTE. LTD. to you or
                  third parties (as applicable);
                </li>
                <li>
                  Customer relationship management (including but not limited to loyalty programs or privilege and
                  reward programs);
                </li>
                <li>Maintaining your credit and payment history for current and future reference;</li>
                <li>
                  Enhancing, improving, designing or launching existing or new trust and corporate services or related
                  products for your use;
                </li>
                <li>
                  If you have consented (including indicating no objection) to Apexis members and/or entities outside
                  Apexis using your personal data for direct marketing in account applications or other application
                  forms or otherwise, for marketing and related services, products or goods;
                </li>
                <li>Determining the amount of debt owed by you or Apexis;</li>
                <li>Collecting outstanding payments from you;</li>
                <li>Meeting any disclosure requirements or requirements under applicable laws and regulations;</li>
                <li>
                  Any other purposes disclosed from time to time on the Apexis website or member application forms;
                </li>
                <li>
                  Providing personalized advertising to you on third-party websites (this may involve aggregating your
                  data with others);
                </li>
                <li>
                  Initiating, defending or otherwise participating in any legal or administrative proceedings or
                  investigations before any court or competent authority;
                </li>
                <li>
                  Seeking or obtaining banking, financing, insurance, administrative, custody, telecommunications,
                  computer, payment, debt collection or securities clearing, auditing, business consulting, outsourcing
                  or other services related to Apexis business operations;
                </li>
                <li>Any other lawful purpose directly or indirectly related to or incidental to any of the above.</li>
              </ul>
            </section>

            <!-- Categories of Potential Transferees -->
            <section class="content-section">
              <h2 class="section-heading">Categories of Potential Transferees</h2>
              <p class="section-paragraph">
                Apexis will endeavor to maintain the confidentiality of your or (if applicable) third party personal
                data held by Apexis, but Apexis may at its discretion make such information available to the following
                persons for direct marketing purposes (with your consent - including indicating no objection) or any
                other purposes permitted by this Privacy Policy:
              </p>
              <ul class="content-list">
                <li>
                  Any local or overseas law enforcement agency, industry organization, regulatory body or authority;
                </li>
                <li>
                  Any agent, contractor or third-party service provider (whether in Singapore or elsewhere) providing
                  Apexis with banking, financing, insurance, administrative, custody, telecommunications, computer,
                  payment, debt collection or securities clearing, auditing, business consulting, outsourcing or other
                  services related to its business operations;
                </li>
                <li>Any branch or office of any Apexis member, whether in Singapore or elsewhere;</li>
                <li>Any person acting or proposing to act as guarantor;</li>
                <li>
                  Any person who owes a duty of confidentiality to Apexis (or any member of the group) or has
                  undertaken to keep such information confidential;
                </li>
                <li>Any financial institution with which you have or plan to have business dealings;</li>
                <li>Any third party with whom you consent to us sharing data;</li>
                <li>Credit reference agencies, and in case of default, debt collection agencies;</li>
                <li>Any other purposes disclosed from time to time on the Apexis website or application forms;</li>
                <li>Any actual or proposed assignee or transferee of any Apexis member;</li>
                <li>
                  Disclosure to any person pursuant to applicable legal and regulatory requirements (including through
                  or pursuant to any rules, judgments, decisions or awards of any court, tribunal, government,
                  regulatory body or other authority), whether or not required under applicable legal and regulatory
                  requirements applicable to any Apexis member;
                </li>
                <li>
                  Apexis may from time to time transfer data relating to you to places outside Singapore and share
                  anonymized data with parties not listed above for any of the above purposes.
                </li>
              </ul>
            </section>

            <!-- Personal Information We May Collect -->
            <section class="content-section">
              <h2 class="section-heading">Personal Information We May Collect</h2>
              <p class="section-paragraph">
                We collect your data in the following circumstances, including: (i) when you interact with us and use
                our products and services; (ii) when you visit our website; and (iii) collecting your data from other
                individuals and companies (including other Apexis companies). This may include (but is not limited to)
                the following personal data:
              </p>
              <ul class="content-list">
                <li>
                  Transaction data regarding bank account details and payments between you and other details of products
                  and services you purchase from us;
                </li>
                <li>
                  Technical data, including Internet Protocol (IP) address, your login data, browser type and version,
                  time zone settings and location, browser plug-in types and versions, operating system and platform and
                  other technology on the devices you use to access our website;
                </li>
                <li>Biometric data, such as your voice ID, fingerprint and facial recognition data;</li>
                <li>Geographic and location data based on your mobile or other electronic devices;</li>
                <li>Data from persons acting on your behalf or persons with whom you deal through our services;</li>
                <li>
                  And data from public sources, credit reference, debt collection and fraud prevention agencies and
                  other aggregators.
                </li>
              </ul>
            </section>

            <!-- Cookies and Automatic Data Collection -->
            <section class="content-section">
              <h2 class="section-heading">Cookies and Automatic Data Collection</h2>
              <p class="section-paragraph">
                If you do not provide us with data, we may not be able to provide products or services. We may also
                generate your data by: (i) combining information Apexis has already collected about you; (ii) based on
                analysis of your interactions with us; and (iii) using cookies and similar technologies when you visit
                our website or applications.
              </p>
              <p class="section-paragraph">
                When you interact with our website, we automatically collect technical data about your device, browsing
                behavior and patterns. We collect this personal data through the use of cookies, server logs and other
                similar technologies. We may also receive technical data about you if you visit other websites that use
                our cookies.
              </p>
            </section>

            <!-- Direct Marketing -->
            <section class="content-section">
              <h2 class="section-heading">Direct Marketing</h2>
              <p class="section-paragraph">
                We will use your data to send you details of financial, insurance or related products, services and
                offers provided by us or our co-branded, rewards or loyalty program partners or charities. We may use
                data such as your demographic data, products and services you are interested in, transaction behavior,
                portfolio information, location data, social media data, analytics data and information from third
                parties to market to you.
              </p>
            </section>

            <!-- Access and Correction of Personal Data -->
            <section class="content-section">
              <h2 class="section-heading">Access and Correction of Personal Data</h2>
              <p class="section-paragraph">
                You have the right to request access to, correction and deletion of information held by Apexis about
                you, and in certain circumstances to object to and restrict the processing of your personal data. You
                also have the right to withdraw your consent to the use of your personal data (although this may affect
                the services we provide to you). If you wish to access or correct your personal data held by Apexis,
                please send your written request to our Privacy Data Officer at
                <a href="mailto:info@apexis.me" class="inline-link">info@apexis.me</a>.
                Apexis may charge a reasonable fee for processing any data access request.
              </p>
            </section>

            <!-- Language -->
            <section class="content-section">
              <h2 class="section-heading">Language</h2>
              <p class="section-paragraph">
                If this agreement is translated from English into any other language, the English version shall prevail
                in the event of any inconsistency.
              </p>
            </section>

            <div class="footer-meta">
              <p class="last-updated">Last Updated: September 2025</p>
            </div>
          </div>
        </article>
      </div>
    </main>
    <footerPart />
  </div>
</template>

<script>
import headerPart from "../components/header.vue";
import footerPart from "../components/footer.vue";

export default {
  name: "PrivacyPage",
  components: {
    headerPart,
    footerPart,
  },
};
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  background: #f9fafb;
}

.content-container {
  max-width: 1024px;
  margin: 0 auto;
  padding: 48px 24px;
  text-align: left;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #4b5563;
  text-decoration: none;
  transition: color 0.2s;
  margin-bottom: 32px;
}

.back-link:hover {
  color: #8b3a3a;
}

.back-icon {
  width: 16px;
  height: 16px;
}

.content-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  padding: 48px;
  text-align: left;
}

.page-title {
  font-size: 30px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.content-body {
  display: flex;
  flex-direction: column;
  gap: 32px;
  color: #374151;
  line-height: 1.6;
}

.content-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-heading {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}

.section-paragraph {
  font-size: 16px;
  color: #374151;
  line-height: 1.6;
}

.content-list {
  list-style: disc;
  padding-left: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.content-list li {
  font-size: 16px;
  color: #374151;
  line-height: 1.6;
}

.inline-link {
  color: #8b3a3a;
  text-decoration: none;
}

.inline-link:hover {
  text-decoration: underline;
}

.footer-meta {
  padding-top: 24px;
  margin-top: 32px;
  border-top: 1px solid #e5e7eb;
}

.last-updated {
  font-size: 14px;
  color: #6b7280;
}

@media screen and (max-width: 768px) {
  .content-container {
    padding: 32px 16px;
  }

  .content-card {
    padding: 32px 24px;
  }

  .page-title {
    font-size: 24px;
  }

  .section-heading {
    font-size: 18px;
  }

  .section-paragraph,
  .content-list li {
    font-size: 14px;
  }
}
</style>
